/**
 * WelcomeBackStats.tsx — Sprint 63
 *
 * Surfaces the Welcome-Back season stats endpoint (Sprint 20.3) for moderators
 * tuning the Tier B threshold (final_season_score > 500).
 *
 * Endpoint:
 *   GET /admin/welcome-back/stats?season=N  (moderator+)
 *
 * Renders:
 *   - Season picker (number input + Load button)
 *   - Total accounts, Tier A / Tier B counts, average final score
 *   - Histogram of distribution buckets (text bar chart)
 *
 * States: loading, error, empty (404/no rows), endpoint-missing (501/404
 * for unmounted server).
 */

import { useState } from 'react';
import {
  getWelcomeBackStats,
  WelcomeBackSeasonStats,
  AdminApiError,
} from '../api/admin-client';

const DEFAULT_SEASON = 1;
const MAX_SEASON = 99;

function fmtScore(n: number | null): string {
  if (n == null) return '—';
  return n.toFixed(1);
}

function pctBar(count: number, max: number): string {
  if (max <= 0) return '';
  const pct = Math.max(2, Math.round((count / max) * 100));
  return `${pct}%`;
}

export default function WelcomeBackStats() {
  const [season, setSeason] = useState<number>(DEFAULT_SEASON);
  const [seasonInput, setSeasonInput] = useState<string>(String(DEFAULT_SEASON));
  const [stats, setStats] = useState<WelcomeBackSeasonStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [endpointMissing, setEndpointMissing] = useState(false);

  async function load(targetSeason: number) {
    if (loading) return;
    setLoading(true);
    setError('');
    setStats(null);
    setEndpointMissing(false);
    try {
      const data = await getWelcomeBackStats(targetSeason);
      setStats(data);
    } catch (err: any) {
      if (err instanceof AdminApiError && (err.status === 404 || err.status === 501)) {
        // 404 on this endpoint means "no data for that season" rather than
        // "endpoint missing" because the route always exists. Backend treats
        // missing rows by returning zeroed stats — so a true 404 here implies
        // either a typo'd path or the server is older than Sprint 20.3.
        setEndpointMissing(true);
      } else if (err instanceof AdminApiError && err.code === 'validation_error') {
        setError(err.message ?? 'Validation error');
      } else {
        setError(err?.message ?? 'Failed to load welcome-back stats');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const n = parseInt(seasonInput, 10);
    if (!Number.isInteger(n) || n <= 0 || n > MAX_SEASON) {
      setError(`Season must be an integer between 1 and ${MAX_SEASON}`);
      return;
    }
    setSeason(n);
    load(n);
  }

  // Compute max bucket count for bar scaling
  const maxBucket = stats
    ? stats.distribution.reduce((m, b) => Math.max(m, b.count), 0)
    : 0;

  return (
    <div>
      <div className="page-title">Welcome-Back Season Stats</div>

      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <div className="form-group" style={{ marginBottom: 0, maxWidth: 140 }}>
          <label htmlFor="wb-season">Season number</label>
          <input
            id="wb-season"
            type="number"
            min={1}
            max={MAX_SEASON}
            value={seasonInput}
            onChange={(e) => setSeasonInput(e.target.value)}
            placeholder="e.g. 1"
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? <span className="spinner" /> : null}
          Load
        </button>
      </form>

      {/* Endpoint-missing banner (rare — older server build) */}
      {endpointMissing && (
        <div className="stub-banner">
          Endpoint <code>GET /admin/welcome-back/stats</code> is not available on
          this server build. Backend reference: Sprint 20.3 admin-routes.mjs.
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      {!loading && !stats && !error && !endpointMissing && (
        <div className="empty-state">
          Enter a season number and press Load to fetch stats.
        </div>
      )}

      {stats && (
        <>
          <div className="card">
            <div className="card-title">
              Season {stats.season_id}
              <span
                className="badge badge-blue"
                style={{ marginLeft: 12, fontSize: 11, fontWeight: 'normal' }}
              >
                {stats.total_accounts} accounts
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 16,
                marginTop: 8,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontWeight: 600,
                  }}
                >
                  Tier A (any returning)
                </div>
                <div style={{ fontSize: 24, fontWeight: 600 }}>{stats.tier_a_count}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontWeight: 600,
                  }}
                >
                  Tier B (score &gt; 500)
                </div>
                <div style={{ fontSize: 24, fontWeight: 600 }} className="status-warn">
                  {stats.tier_b_count}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontWeight: 600,
                  }}
                >
                  Avg final score
                </div>
                <div style={{ fontSize: 24, fontWeight: 600 }}>
                  {fmtScore(stats.average_final_score)}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontWeight: 600,
                  }}
                >
                  Tier B %
                </div>
                <div style={{ fontSize: 24, fontWeight: 600 }}>
                  {stats.total_accounts > 0
                    ? `${Math.round((stats.tier_b_count / stats.total_accounts) * 100)}%`
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Distribution histogram */}
          <div className="card mt-16">
            <div className="card-title">Final Score Distribution</div>
            {stats.distribution.length === 0 || stats.total_accounts === 0 ? (
              <div className="empty-state">No accounts recorded for this season yet.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>Bucket</th>
                      <th style={{ width: 80, textAlign: 'right' }}>Count</th>
                      <th>Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.distribution.map((b) => {
                      // Tier B threshold sits at 500; visually mark buckets
                      // that are above the threshold for quick read.
                      const isTierB =
                        b.bucket === '500-699' ||
                        b.bucket === '700-999' ||
                        b.bucket === '1000+';
                      return (
                        <tr key={b.bucket}>
                          <td>
                            <span
                              className={isTierB ? 'status-warn' : ''}
                              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                            >
                              {b.bucket}
                            </span>
                            {isTierB && (
                              <span
                                className="badge badge-amber"
                                style={{ marginLeft: 6, fontSize: 9 }}
                              >
                                Tier B
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>{b.count}</td>
                          <td>
                            <div className="wb-bar-track">
                              <div
                                className={`wb-bar-fill ${isTierB ? 'wb-bar-tier-b' : ''}`}
                                style={{ width: pctBar(b.count, maxBucket) }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        .wb-bar-track {
          background: var(--bg-elevated);
          border-radius: 4px;
          height: 14px;
          width: 100%;
          overflow: hidden;
        }
        .wb-bar-fill {
          background: var(--accent);
          height: 100%;
          transition: width 0.2s ease-out;
        }
        .wb-bar-tier-b {
          background: var(--warning, #f59e0b);
        }
      `}</style>
    </div>
  );
}
